/**
 * Credits used on the open video (F8 §3.3): the total the paying wallet was charged for it, linking to the billing Activity.
 * Hidden while the video has no charge: a video made before charging began (16 Oct 2026) has no total to show.
 * Studio B shows it on the bar (`b`) as a small coin chip with the amount, its words in a hover tip, where the bar has room:
 * below 1280 px it gives way to Ivy's progress chip and the out-of-credits alert; not on an upright iPad's full bar (768–1023 px).
 * There it sits at the foot of Ivy's column instead (`b-foot`).
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Coins } from 'lucide-react';
import { IconTipB } from '@/components/studio/IconTipB';
import { billingAPI } from '@/services/api';
import { ACTIVITY_ANCHOR, formatCreditAmount } from '@/components/billing/CreditActivity';

export function VideoCreditsUsed({ conversationId, variant = 'a' }: { conversationId: string; variant?: 'a' | 'b' | 'b-foot' }) {
  const [used, setUsed] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUsed(null);
    billingAPI
      .getActivityByVideo({ videoId: conversationId })
      .then((r) => {
        if (!cancelled) setUsed(r.videos[0]?.charged ?? null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [conversationId]);

  if (used === null) return null;

  const amount = formatCreditAmount(used);
  if (variant === 'b') {
    const words = `Credits used on this video: ${amount}`;
    return (
      <IconTipB label={words}>
        <Link
          to="/account/billing"
          hash={ACTIVITY_ANCHOR}
          aria-label={words}
          className="flex h-7 flex-shrink-0 items-center gap-1 rounded-full px-2 text-b-meta tabular-nums text-b-t2 shadow-[inset_0_0_0_1px_hsl(var(--b-line-strong))] transition-colors hover:bg-b-raised hover:text-b-t1 md:max-lg:hidden max-xl:[[data-studio-topbar=b]:has([data-progress-chip])_&]:hidden max-xl:[[data-studio-topbar=b]:has([data-credits-alert])_&]:hidden"
        >
          <Coins className="h-3.5 w-3.5" />
          {amount}
        </Link>
      </IconTipB>
    );
  }

  if (variant === 'b-foot') {
    return (
      <Link
        to="/account/billing"
        hash={ACTIVITY_ANCHOR}
        className="hidden min-w-0 truncate text-b-meta tabular-nums text-b-t2 transition-colors hover:text-b-t1 md:max-lg:inline-block"
      >
        Credits used on this video: {amount} · Activity
      </Link>
    );
  }

  return (
    <Link
      to="/account/billing"
      hash={ACTIVITY_ANCHOR}
      title="See this video's credit activity"
      className="px-2 text-xs tabular-nums studio-text-secondary hover:studio-text-primary"
    >
      Credits used: {formatCreditAmount(used)}
    </Link>
  );
}
